import React, { useState, useEffect, useMemo } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, Select, Button, Table, TextInput, Pagination, Card } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getAllCountryList } from '../../../../services/location'
import { useNavigate } from 'react-router-dom'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import { IconSearch } from '@tabler/icons-react'

const SOURCE_COUNTRIES = [
    { value: '44', label: 'China' },
    { value: '99', label: 'Indonesia' },
    { value: '208', label: 'Thailand' },
    { value: '106', label: 'Japan' },
    { value: '187', label: 'Singapore' },
    { value: '128', label: 'Malaysia' },
    { value: '167', label: 'Philippines' },
    { value: '222', label: 'United States (US)' },
    { value: '13', label: 'Australia' },
    { value: '111', label: 'North Korea' },
    { value: '229', label: 'Viet Nam' }
]

const InternationalShippingRateList = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Default to the first source country (Thailand)
    const [sourceCountryId, setSourceCountryId] = useState(SOURCE_COUNTRIES[0].value)

    // Destination params - fetching from API
    const [destinationParams, setDestinationParams] = useState({
        'page[num]': 1,
        'page[limit]': 10,
        sort: 'name',
    })

    const [destinationCountries, setDestinationCountries] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [destinationSearch, setDestinationSearch] = useState('')

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null },
        { label: 'International Shipping Rate List', route: null },
    ]

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))
    }, [dispatch])

    const fetchDestinationCountries = async () => {
        setLoading(true)
        try {
            // Apply search filter if exists
            const params = { ...destinationParams }
            if (destinationSearch) {
                params['filter[name][like]'] = destinationSearch
            }

            const response = await getAllCountryList(params)
            if (response.success) {
                setDestinationCountries(response.data)
                setTotalData(response.meta.pagination.total)
            } else {
                setDestinationCountries([])
                setTotalData(0)
            }
        } catch (error) {
            console.error(error)
            setDestinationCountries([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchDestinationCountries()
    }, [destinationParams, destinationSearch])

    const handleSearchChange = (e) => {
        const value = e.target.value
        setDestinationSearch(value)
        setDestinationParams(prev => ({ ...prev, 'page[num]': 1 }))
    }

    const handlePageChange = (val) => {
        setDestinationParams(prev => ({ ...prev, 'page[num]': val }))
    }

    const selectedSourceName = useMemo(() => {
        const found = SOURCE_COUNTRIES.find(c => c.value === String(sourceCountryId))
        return found ? found.label : '-'
    }, [sourceCountryId])

    const handleSetRate = (destinationCountry) => {
        navigate(`/settings/international-shipping-rates/set-rates?source_id=${sourceCountryId}&dest_id=${destinationCountry.attributes.id}`)
    }

    const rows = destinationCountries.map((country) => (
        <Table.Tr key={country.id}>
            <Table.Td>{selectedSourceName}</Table.Td>
            <Table.Td>{country.attributes.name}</Table.Td>
            <Table.Td>
                <Button
                    size="xs"
                    onClick={() => handleSetRate(country)}
                >
                    Set Rate
                </Button>
            </Table.Td>
        </Table.Tr>
    ))

    return (
        <AuthLayout>
            <Box my={20}>
                <Flex justify='space-between' align="center" mb={30}>
                    <Text className={classes.titleLayout}>International Shipping Rate List</Text>
                </Flex>

                <Card>
                    <Group mb="md" grow>
                        <Select
                            label="From (Source Country)"
                            placeholder="Select Source Country"
                            data={SOURCE_COUNTRIES}
                            value={String(sourceCountryId)}
                            onChange={setSourceCountryId}
                            searchable
                            checkBoxProps
                        />
                        <TextInput
                            label="To (Destination Country)"
                            placeholder="Search Destination Country..."
                            value={destinationSearch}
                            onChange={handleSearchChange}
                            leftSection={<IconSearch size={16} />}
                        />
                    </Group>

                    <Box>
                        {loading ? (
                            <LoadingData />
                        ) : destinationCountries.length > 0 ? (
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>From</Table.Th>
                                        <Table.Th>To</Table.Th>
                                        <Table.Th>Action</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>{rows}</Table.Tbody>
                            </Table>
                        ) : (
                            <NoData />
                        )}
                    </Box>

                    <Flex justify='end' mt="md">
                        <Pagination
                            size='sm'
                            onChange={handlePageChange}
                            value={destinationParams['page[num]']}
                            total={totalData > 0 ? Math.ceil(totalData / destinationParams['page[limit]']) : 0}
                        />
                    </Flex>
                </Card>
            </Box>
        </AuthLayout>
    )
}

export default InternationalShippingRateList
